import React, { FunctionComponent } from "react";
import styles from "./styles.module.scss";
import { Card, Fade, LinearProgress, List, Stack, useTheme } from "@mui/material";
import { IStats } from "../../../hooks/stats/useCreateStats";
import { RightDetailListItem } from "../../RightDetailListItem";
import { strings } from "../../../localization/LocalizedStrings";
import { TimeInterval } from "../../../models/TimeInterval";
import { StatsGraphs } from "../StatsGraphs";
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels";
import { useIsMobile } from "../../../hooks/isMobile";
import { useCurrencyFormatter } from "../../../hooks/currencyFormatter";
import { useUserProfile } from "../../../hooks/session";
import { Icon, IconType } from "../../../icons";
import { PiperDivider } from "../../PiperDivider";
import { StatsCountsExpandableSection } from "../StatsCountsExpandableSection";

export const StatsDetail: FunctionComponent<IStatsProps> = ({ stats, isLoading }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const currencyFormatter = useCurrencyFormatter();
  const [currentUserProfile] = useUserProfile.useState();
  const statsList = (stats: IStats) => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Card
        elevation={5}
        className={isMobile ? styles.itemCardMobile : styles.itemCard}
        key={"general_card"}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <RightDetailListItem
          style={{ backgroundColor: theme.palette.background.default }}
          title={strings.stats_general_title}
          key={"general_title"}
          boldTitle
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_album_total}
          detail={String(stats.totalAlbumCount)}
          key={"general_item_1"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_box_set_total}
          detail={String(stats.totalBoxSetCount)}
          key={"general_item_0"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_clean_total}
          detail={String(stats.cleanCount)}
          key={"general_item_2"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_gatefold_total}
          detail={String(stats.gatefoldCount)}
          key={"general_item_3"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_insert_total}
          detail={String(stats.insertCount)}
          key={"general_item_4"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_lyrics_total}
          detail={String(stats.lyricsCount)}
          key={"general_item_5"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_colored_total}
          detail={String(stats.coloredAlbumsCount)}
          key={"general_item_6"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_average_original_year}
          detail={String(Math.round(stats.averageOriginalYear))}
          key={"general_item_7"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_average_release_year}
          detail={String(Math.round(stats.averageReleaseYear))}
          key={"general_item_8"}
        />
      </Card>
      <Card
        elevation={5}
        className={isMobile ? styles.itemCardMobile : styles.itemCard}
        key={"length_card"}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <RightDetailListItem
          style={{ backgroundColor: theme.palette.background.default }}
          title={strings.album_length_title}
          key={"length_title"}
          boldTitle
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_discs_total}
          detail={String(stats.totalDiscs)}
          key={"length_item_1"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_length_total}
          detail={TimeInterval.toHHMMSS(stats.totalLength)}
          key={"length_item_2"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_average_album_length}
          detail={TimeInterval.toMMSS(Math.round(stats.albumAverageLength))}
          key={"length_item_3"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_average_disc_length}
          detail={
            stats.discAverageLength
              ? TimeInterval.toMMSS(Math.round(stats.discAverageLength))
              : strings.unknown
          }
          key={"length_item_4"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_average_track_length}
          detail={
            stats.trackAverageLength
              ? TimeInterval.toMMSS(Math.round(stats.trackAverageLength))
              : strings.unknown
          }
          key={"length_item_5"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_tracks_by_album}
          detail={String(Math.round(stats.tracksByAlbum))}
          key={"length_item_6"}
        />
      </Card>
      {(stats.artistCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection
          sectionName={"artists"}
          title={strings.attribute_artists_title}
        >
          {stats.artistCounts
            ?.sort((a, b) => b.count - a.count)
            .map((artistCount, index) => (
              <div key={`artist_div_${index}`}>
                <RightDetailListItem
                  title={artistCount.grade.name}
                  detail={String(artistCount.count)}
                  key={`artist_item_${index}`}
                />
                {index < (stats.artistCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.labelCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection sectionName={"labels"} title={strings.attribute_labels_title}>
          {stats.labelCounts
            ?.sort((a, b) => b.count - a.count)
            .map((labelCount, index) => (
              <div key={`label_div_${index}`}>
                <RightDetailListItem
                  title={labelCount.grade.name}
                  detail={String(labelCount.count)}
                  key={`label_item_${index}`}
                />
                {index < (stats.labelCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.originCountryCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection
          sectionName={"origin_countries"}
          title={strings.origin_countries_title}
        >
          {stats.originCountryCounts
            ?.sort((a, b) => b.count - a.count)
            .map((originCountryCount, index) => (
              <div key={`origin_country_div_${index}`}>
                <RightDetailListItem
                  title={originCountryCount.grade.name}
                  detail={String(originCountryCount.count)}
                  key={`origin_country_item_${index}`}
                />
                {index < (stats.originCountryCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.storeCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection sectionName={"stores"} title={strings.attribute_stores_title}>
          {stats.storeCounts
            ?.sort((a, b) => b.count - a.count)
            .map((storeCount, index) => (
              <div key={`store_div_${index}`}>
                <RightDetailListItem
                  title={storeCount.grade.name}
                  detail={String(storeCount.count)}
                  key={`store_item_${index}`}
                />
                {index < (stats.storeCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.albumTypeCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection
          sectionName={"album_types"}
          title={strings.attribute_album_types_title}
        >
          {stats.albumTypeCounts
            ?.sort((a, b) => b.count - a.count)
            .map((albumTypeCount, index) => (
              <div key={`album_type_div_${index}`}>
                <RightDetailListItem
                  title={albumTypeCount.grade.name}
                  detail={String(albumTypeCount.count)}
                  key={`album_type_item_${index}`}
                />
                {index < (stats.albumTypeCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.albumFormatCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection
          sectionName={"album_formats"}
          title={strings.attribute_album_formats_title}
        >
          {stats.albumFormatCounts
            ?.sort((a, b) => b.count - a.count)
            .map((albumFormatCount, index) => (
              <div key={`album_format_div_${index}`}>
                <RightDetailListItem
                  title={albumFormatCount.grade.name}
                  detail={String(albumFormatCount.count)}
                  key={`album_format_item_${index}`}
                />
                {index < (stats.albumFormatCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      {(stats.tagCounts?.length ?? 0) > 0 && (
        <StatsCountsExpandableSection sectionName={"tags"} title={strings.tags_title}>
          {stats.tagCounts
            ?.sort((a, b) => b.count - a.count)
            .map((tagCount, index) => (
              <div key={`album_tag_div_${index}`}>
                <RightDetailListItem
                  title={tagCount.grade.name}
                  detail={String(tagCount.count)}
                  key={`album_tag_item_${index}`}
                />
                {index < (stats.tagCounts?.length ?? 0) - 1 && <PiperDivider />}
              </div>
            ))}
        </StatsCountsExpandableSection>
      )}
      <Card
        elevation={5}
        className={isMobile ? styles.itemCardMobile : styles.itemCard}
        key={"financials_card"}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <RightDetailListItem
          title={strings.stats_financials_title}
          style={{ backgroundColor: theme.palette.background.default }}
          key={"financials_title"}
          boldTitle
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_purchase_price_total}
          detail={currencyFormatter.format(stats.totalPurchasePrice, currentUserProfile, false)}
          key={"financials_item_1"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_sell_value_total}
          detail={currencyFormatter.format(stats.totalSellValue, currentUserProfile, false)}
          key={"financials_item_2"}
        />
        <PiperDivider />
        <RightDetailListItem
          title={strings.stats_revenue_total}
          detail={currencyFormatter.format(
            stats.totalSellValue - stats.totalPurchasePrice,
            currentUserProfile,
            false
          )}
          key={"financials_item_3"}
        />
      </Card>
    </div>
  );
  return (
    <Card
      className={styles.mainContainer}
      style={{ backgroundColor: theme.palette.background.default }}
    >
      {stats ? (
        <PanelGroup className={styles.mainDiv} orientation={"horizontal"}>
          {!isMobile && (
            <Panel
              minSize={"30"}
              defaultSize={"34"}
              maxSize={"48"}
              style={{ display: "flex", overflow: "auto", paddingBottom: 16 }}
            >
              <List disablePadding className={styles.list}>
                {statsList(stats)}
              </List>
            </Panel>
          )}
          {!isMobile && (
            <PanelResizeHandle className={styles.resizeHandle}>
              <Icon
                type={IconType.DragIndicator}
                style={{ alignSelf: "center", color: theme.palette.secondary.main, fontSize: 14 }}
              />
            </PanelResizeHandle>
          )}
          <Panel className={isMobile ? styles.graphsDivMobile : styles.graphsDiv}>
            {isMobile && statsList(stats)}
            <StatsGraphs stats={stats} />
          </Panel>
        </PanelGroup>
      ) : (
        <Fade in unmountOnExit style={{ transitionDelay: "2000ms" }}>
          <Stack className={styles.loadingStack} spacing={3}>
            <p>{isLoading ? strings.stats_loading : strings.stats_error}</p>
            {isLoading && (
              <LinearProgress className={styles.progressBar} variant={"indeterminate"} />
            )}
          </Stack>
        </Fade>
      )}
    </Card>
  );
};

export interface IStatsProps {
  stats?: IStats;
  isLoading: boolean;
}
